/**
 * Sort orders for the List view (Boards Redesign card 6.3, 12.10.2026).
 *
 * Pure, so the orders are unit-tested without rendering a board. Sorting only
 * changes what the rows are DRAWN in: it never writes a position back into a
 * task (Linear's view ordering works the same way), and every order ends in
 * the manual one, so two tasks that tie on the key keep the order they have
 * on the board and the result is identical on every client.
 *
 * "Manual" is the board's own order (drag and drop). Any other order turns
 * drag-to-reorder off in the list, because a drop could not land where it
 * was dropped: the sort would put the row straight back.
 */

export const SORT_MANUAL = 'manual';
export const SORT_PRIORITY = 'priority';
export const SORT_DUE = 'due';
export const SORT_TITLE = 'title';
export const SORT_UPDATED = 'updated';
export const SORT_CREATED = 'created';

/** Picker options, in menu order. "Sort: …" like the Boards overview's
 *  picker (card 7.2), so the trigger says what it is without a second label. */
export const LIST_SORT_OPTIONS = Object.freeze([
  { value: SORT_MANUAL, label: 'Sort: Manual' },
  { value: SORT_PRIORITY, label: 'Sort: Priority' },
  { value: SORT_DUE, label: 'Sort: Due date' },
  { value: SORT_TITLE, label: 'Sort: Title' },
  { value: SORT_UPDATED, label: 'Sort: Recently updated' },
  { value: SORT_CREATED, label: 'Sort: Newest' },
]);

const KEYS = new Set(LIST_SORT_OPTIONS.map((o) => o.value));

export function isListSortKey(key) {
  return KEYS.has(key);
}

/** An unknown or missing key (old prefs, a typo) is the manual order. */
export function normalizeListSort(key) {
  return isListSortKey(key) ? key : SORT_MANUAL;
}

/** Same rule as boardsStore.compareTasksByPosition (duplicated here so this
 *  module stays free of the records/Tauri imports of boardsStore). */
export function compareByPosition(a, b) {
  return (a.position ?? 0) - (b.position ?? 0) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

const PRIORITY_RANK = { high: 0, medium: 1, low: 2 };

function priorityRank(task) {
  return PRIORITY_RANK[task?.priority] ?? PRIORITY_RANK.medium;
}

function timeOf(value) {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
}

/** `YYYY-MM-DD` compares correctly as text; anything else goes through Date. */
function dueKey(task) {
  const d = task?.dueDate;
  if (!d) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(d))) return String(d);
  const t = timeOf(d);
  return t == null ? null : new Date(t).toISOString().slice(0, 10);
}

/** Missing values go last in every direction, like Linear and Trello. */
function nullsLast(a, b, cmp) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return cmp(a, b);
}

const collator = typeof Intl !== 'undefined' ? new Intl.Collator(undefined, { sensitivity: 'base', numeric: true }) : null;

function compareText(a, b) {
  return collator ? collator.compare(a, b) : a < b ? -1 : a > b ? 1 : 0;
}

const COMPARATORS = {
  [SORT_PRIORITY]: (a, b) => priorityRank(a) - priorityRank(b),
  [SORT_DUE]: (a, b) => nullsLast(dueKey(a), dueKey(b), (x, y) => (x < y ? -1 : x > y ? 1 : 0)),
  [SORT_TITLE]: (a, b) => nullsLast((a.title || '').trim() || null, (b.title || '').trim() || null, compareText),
  [SORT_UPDATED]: (a, b) => nullsLast(timeOf(a.updatedAt), timeOf(b.updatedAt), (x, y) => y - x),
  [SORT_CREATED]: (a, b) => nullsLast(timeOf(a.createdAt), timeOf(b.createdAt), (x, y) => y - x),
};

/** A NEW array of `tasks` in `sortKey` order (the input is not touched). */
export function sortListTasks(tasks, sortKey) {
  const list = Array.isArray(tasks) ? [...tasks] : [];
  const cmp = COMPARATORS[normalizeListSort(sortKey)];
  return list.sort(cmp ? (a, b) => cmp(a, b) || compareByPosition(a, b) : compareByPosition);
}
